import {useMutation, useQueryClient} from '@github-ui/react-query'
import {useAddBanner, useFocusBanner} from '@github-ui/banner-stack'
import type {QueryKey} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

type ActionsNotEnabledBannerCacheData = {
  payload: {
    showActionsNotEnabledBanner: boolean
  }
}

type ActionsEnableResponse = {
  message: string
}

/**
 *
 */
export function useActionsEnable({queryKey, path}: {queryKey: QueryKey; path: string}) {
  const queryClient = useQueryClient()
  const addBanner = useAddBanner()
  const focusBanner = useFocusBanner()

  return useMutation<ActionsEnableResponse, Error, void>({
    mutationFn: async () => {
      const response = await verifiedFetchJSON(path, {
        method: 'POST',
      })
      const data: ActionsEnableResponse = await response.json()
      if (!response.ok) {
        throw new Error(data.message)
      }
      return data
    },
    onSuccess: data => {
      const message = data.message ?? 'GitHub Actions has been enabled'
      const id = addBanner({
        title: message,
        variant: 'success',
        dismissible: true,
      })

      requestAnimationFrame(() => focusBanner(id))
    },
    onError: error => {
      const id = addBanner({
        title: error.message ?? 'There was an error enabling GitHub Actions',
        variant: 'critical',
        dismissible: true,
      })
      requestAnimationFrame(() => focusBanner(id))
    },
    onSettled: () => {
      queryClient.setQueryData<ActionsNotEnabledBannerCacheData>(queryKey, oldData => {
        if (!oldData) return oldData
        return {
          ...oldData,
          payload: {
            ...oldData.payload,
            showActionsNotEnabledBanner: false,
          },
        }
      })
    },
  })
}
